import type { FundBasket, FundBasketAnalytics } from "creso-core/types";
import {
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "web-design-system";

export function FundBasketOverlap({
  overlap,
}: {
  overlap: FundBasketAnalytics["overlap"];
  basket: FundBasket;
}) {
  const { funds } = overlap;
  const { overlap_matrix } = overlap;
  const n = funds.length;

  if (n < 2) return null;

  const rowIndices = Array.from({ length: n - 1 }, (_, i) => n - 1 - i);

  return (
    <div className="flex flex-col gap-3 break-inside-avoid">
      <span className="text-base font-semibold text-foreground">
        Portfolio Overlap
      </span>
      <div className="w-full overflow-x-auto rounded-lg border border-border bg-muted">
        <Table className="table-fixed rounded-none border-collapse">
          <TableHeader>
            <TableRow className="border-border bg-card hover:bg-card">
              <TableCell
                scope="col"
                className="sticky left-0 z-20 w-40 border-r border-border bg-card font-normal"
              >
                <span className="sr-only">Fund</span>
              </TableCell>
              {funds.map((fund) => (
                <TableCell key={fund.fund_id} scope="col" className="w-30">
                  <span className="line-clamp-2">{fund.name}</span>
                </TableCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rowIndices.map((rowIdx) => (
              <TableRow
                key={rowIdx}
                className="border-border bg-background hover:bg-background"
              >
                <TableHead
                  scope="row"
                  className="sticky left-0 z-10 w-40 border-r border-border bg-card font-normal"
                >
                  <span className="line-clamp-2">{funds[rowIdx]?.name}</span>
                </TableHead>
                {Array.from({ length: n }).map((_, colIdx) => {
                  const showValue = colIdx < rowIdx;
                  const pct = showValue
                    ? overlap_matrix[rowIdx]?.[colIdx]
                    : null;

                  return (
                    <TableCell
                      key={colIdx}
                      className={cn(
                        "w-30",
                        colIdx < n - 1 && "border-r border-border",
                        !showValue && "bg-muted"
                      )}
                    >
                      {showValue && pct !== null && pct !== undefined ? (
                        <div className="flex w-full flex-col gap-1.5">
                          <span className="text-sm font-semibold">
                            {pct.toFixed(2)}%
                          </span>
                          <div className="h-2 w-full overflow-hidden rounded bg-secondary">
                            <div
                              className={cn("h-full rounded-full", {
                                "bg-destructive": true,
                                "bg-chart-3": pct < 50,
                                "bg-constructive": pct < 20,
                              })}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                        </div>
                      ) : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
